import React, { useCallback, useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../api";

import {
  FiTrash2,
  FiEdit,
  FiEye,
  FiPlus,
  FiSave,
  FiPackage,
  FiClipboard,
  FiSearch,
} from "react-icons/fi";

import { toasts } from "../components/AppToast";

const EMPTY_FORM = {
  optitem_name: "",
  optitem_desc: "",
};

export default function ManageOptionalExtra() {
  // Data
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search
  const [search, setSearch] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Form
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Modals
  const [formModal, setFormModal] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [delModal, setDelModal] = useState(null);
  const [delSaving, setDelSaving] = useState(false);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const response = await API.get("/optional-extra", {
        params: {
          page,
          limit,
          search,
        },
      });

      const responseData = response.data;

      if (Array.isArray(responseData)) {
        setData(responseData);
        setTotal(responseData.length);
        setTotalPages(Math.ceil(responseData.length / limit));
      } else {
        setData(responseData.data || []);
        setTotal(Number(responseData.total || 0));

        setTotalPages(
          Number(
            responseData.totalPages ||
              Math.ceil(Number(responseData.total || 0) / limit)
          )
        );
      }
    } catch (error) {
      console.error("fetchData error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to load optional extras"
      );

      setData([]);
      setTotal(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search]);

  // Load data
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Search
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  // Reset form
  const resetForm = () => {
    setForm({
      ...EMPTY_FORM,
    });

    setEditId(null);
  };

  // Open add modal
  const openAddModal = () => {
    resetForm();
    setFormModal(true);
  };

  // Close form modal
  const closeFormModal = () => {
    if (saving) {
      return;
    }

    setFormModal(false);
    resetForm();
  };

  // Edit
  const startEdit = (row) => {
    setForm({
      optitem_name: row.optitem_name || "",
      optitem_desc: row.optitem_desc || "",
    });

    setEditId(row.optitem_id);
    setFormModal(true);
  };

  // View
  const handleView = (row) => {
    setViewModal(row);
  };

  // Form change
  const handleChange = (field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  // Submit
  const handleSubmit = async () => {
    if (!form.optitem_name.trim()) {
      toasts.error("Optional extra name is required");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        optitem_name: form.optitem_name.trim(),
        optitem_desc: form.optitem_desc.trim(),
      };

      if (editId) {
        await API.put(`/optional-extra/${editId}`, payload);

        toasts.success("Optional extra updated successfully");
      } else {
        await API.post("/optional-extra", payload);

        toasts.success("Optional extra added successfully");
      }

      setFormModal(false);
      resetForm();

      await fetchData();
    } catch (error) {
      console.error("Submit error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Operation failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!delModal) {
      return;
    }

    setDelSaving(true);

    try {
      await API.delete(`/optional-extra/${delModal.optitem_id}`);

      toasts.success("Optional extra deleted successfully");

      setDelModal(null);

      if (data.length === 1 && page > 1) {
        setPage((previous) => previous - 1);
      } else {
        await fetchData();
      }
    } catch (error) {
      console.error("Delete error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Delete failed"
      );
    } finally {
      setDelSaving(false);
    }
  };

  // Sequence update
  const handleSequenceChange = async (id, value) => {
    try {
      await API.put(`/optional-extra/${id}/sequence`, {
        seq: Number(value),
      });

      toasts.success("Sequence updated successfully");

      await fetchData();
    } catch (error) {
      console.error("Sequence error:", error);

      toasts.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to update sequence"
      );
    }
  };

  return (
    <Layout activePage="Optional Extra">
      <main className="dashboard-main">
        {/* Table card */}
        <div className="table-card">
          {/* Header */}
          <div className="table-card-head">
            <h3 className="table-card-title d-flex align-items-center">
              <FiClipboard size={26} className="me-2" />

              <span>Manage Optional Extra</span>

              <span className="rec-count ms-1">{total} slabs</span>
            </h3>

            {/* Search */}
            <div className="search-wrapper">
              <span className="search-icon">
                <FiSearch />
              </span>

              <input
                type="text"
                placeholder="Search ..."
                value={search}
                onChange={handleSearch}
                className="search-input"
              />

              {search && (
                <button
                  type="button"
                  className="season-clear-btn"
                  onClick={() => {
                    setSearch("");
                    setPage(1);
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* Add */}
            <button
              type="button"
              className="um-btn-primary"
              onClick={openAddModal}
            >
              <span className="um-add-icon">＋</span>
              Add Optional Extra
            </button>
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="um-table">
              <thead>
                <tr className="um-table-head">
                  <th className="um-th text-center">#</th>
                  <th className="um-th text-center">Optional Extra</th>
                  <th className="um-th text-center">Details</th>
                  <th className="um-th text-center">Sequence</th>
                  <th className="um-th text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {/* Loading */}
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index}>
                      {Array.from({ length: 5 }).map((_, cellIndex) => (
                        <td
                          key={cellIndex}
                          className="um-td text-center"
                        >
                          <div className="skeleton" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : data.length === 0 ? (
                  /* Empty */
                  <tr>
                    <td
                      colSpan={5}
                      className="empty-table-cell text-center py-5"
                    >
                      <div className="empty-icon">
                        <FiPackage />
                      </div>

                      <div className="empty-title">
                        No optional extras found
                      </div>

                      <div className="empty-sub">
                        Use the Add Optional Extra button to add one
                      </div>
                    </td>
                  </tr>
                ) : (
                  /* Data */
                  data.map((row, index) => (
                    <tr
                      key={row.optitem_id}
                      className={`um-row ${
                        index % 2 === 0 ? "even-row" : "odd-row"
                      }`}
                    >
                      {/* Number */}
                      <td className="um-td text-center">
                        {(page - 1) * limit + index + 1}
                      </td>

                      {/* Name */}
                      <td className="um-td text-center">
                        <span className="badge badge-success">
                          {row.optitem_name}
                        </span>
                      </td>

                      {/* Details */}
                      <td className="um-td">
                        <div
                          style={{
                            maxWidth: "350px",
                            margin: "0 auto",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={row.optitem_desc || ""}
                        >
                          {row.optitem_desc || "—"}
                        </div>
                      </td>

                      {/* Sequence */}
                      <td className="um-td text-center">
                        <select
                          name={`cmbsequence-${row.optitem_id}`}
                          className="form-select form-select-sm"
                          value={row.seq ?? 0}
                          onChange={(e) =>
                            handleSequenceChange(
                              row.optitem_id,
                              e.target.value
                            )
                          }
                        >
                          <option value="0">00</option>

                          {Array.from(
                            {
                              length: Math.max(total, 10),
                            },
                            (_, i) => i + 1
                          ).map((number) => (
                            <option key={number} value={number}>
                              {String(number).padStart(2, "0")}
                            </option>
                          ))}
                        </select>
                      </td>

                      {/* Action */}
                      <td className="um-td text-center">
                        <div className="action-buttons">
                          {/* View */}
                          <button
                            type="button"
                            className="action-btn action-view"
                            title="View"
                            onClick={() => handleView(row)}
                          >
                            <FiEye size={15} />
                          </button>

                          {/* Edit */}
                          <button
                            type="button"
                            className="action-btn action-edit"
                            title="Edit"
                            onClick={() => startEdit(row)}
                          >
                            <FiEdit size={15} />
                          </button>

                          {/* Delete */}
                          <button
                            type="button"
                            className="action-btn action-delete delete-enabled"
                            title="Delete"
                            onClick={() => setDelModal(row)}
                          >
                            <FiTrash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 0 && (
              <div className="pagination">
                <span className="pagin-info">
                  Showing {(page - 1) * limit + 1}
                  {" – "}
                  {Math.min(page * limit, total)} of {total}
                </span>

                <div className="d-flex align-items-center gap-2">
                  {/* Previous */}
                  <button
                    type="button"
                    className="pg-btn"
                    disabled={page === 1 || loading}
                    onClick={() =>
                      setPage((previous) =>
                        Math.max(1, previous - 1)
                      )
                    }
                  >
                    ← Prev
                  </button>

                  {/* Page */}
                  <span className="pagin-info">
                    Page {page} / {totalPages}
                  </span>

                  {/* Next */}
                  <button
                    type="button"
                    className="pg-btn"
                    disabled={page >= totalPages || loading}
                    onClick={() =>
                      setPage((previous) =>
                        Math.min(totalPages, previous + 1)
                      )
                    }
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add / Edit Modal */}
        {formModal && (
          <div
            className="um-overlay"
            onClick={closeFormModal}
          >
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="um-modal-head">
                <div className="d-flex align-items-center gap-2">
                  <div className="form-card-icon">
                    {editId ? (
                      <FiEdit size={24} />
                    ) : (
                      <FiPlus size={24} />
                    )}
                  </div>

                  <div>
                    <h2 className="um-modal-title mb-0">
                      {editId
                        ? "Edit Optional Extra"
                        : "Add Optional Extra"}
                    </h2>

                    <p className="form-card-sub mb-0">
                      Manage optional extra information
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  ✕
                </button>
              </div>

              {/* Body */}
              <div className="um-modal-scroll">
                <div className="row g-3">
                  {/* Name */}
                  <div className="col-md-12">
                    <label className="field-label">
                      Optional Extra Name *
                    </label>

                    <input
                      type="text"
                      className="rd-input"
                      style={{ textAlign: "start" }}
                      placeholder="Enter optional extra name"
                      value={form.optitem_name}
                      onChange={(e) =>
                        handleChange(
                          "optitem_name",
                          e.target.value
                        )
                      }
                    />
                  </div>

                  {/* Details */}
                  <div className="col-md-12">
                    <label className="field-label">
                      Details
                    </label>

                    <textarea
                      className="rd-input"
                      style={{ textAlign: "start" }}
                      rows="5"
                      placeholder="Enter optional extra details"
                      value={form.optitem_desc}
                      onChange={(e) =>
                        handleChange(
                          "optitem_desc",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={closeFormModal}
                  disabled={saving}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={handleSubmit}
                  disabled={saving}
                >
                  {saving ? (
                    <span className="spinner" />
                  ) : editId ? (
                    <>
                      <FiSave />
                      Update Optional Extra
                    </>
                  ) : (
                    <>
                      <FiPlus />
                      Add Optional Extra
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {viewModal && (
          <div
            className="um-overlay"
            onClick={() => setViewModal(null)}
          >
            <div
              className="um-modal um-view-modal"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="um-modal-head">
                <div>
                  <h2 className="um-modal-title mb-0">
                    <FiEye /> Optional Extra Details
                  </h2>

                  <p className="form-card-sub mb-0">
                    View optional extra information
                  </p>
                </div>

                <button
                  type="button"
                  className="um-x-btn"
                  onClick={() => setViewModal(null)}
                >
                  ✕
                </button>
              </div>

              {/* Body */}
              <div className="um-modal-scroll">
                <div className="row g-3">
                  {/* Name */}
                  <div className="col-md-12">
                    <div className="small text-muted text-uppercase fw-semibold mb-1">
                      Optional Extra
                    </div>

                    <div className="bg-light rounded-3 border p-3">
                      <div className="fw-bold">
                        {viewModal.optitem_name}
                      </div>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="col-md-12">
                    <div className="small text-muted text-uppercase fw-semibold mb-2">
                      Details
                    </div>

                    <div className="bg-light rounded-3 border p-3">
                      <div
                        className="fw-semibold"
                        style={{
                          whiteSpace: "pre-wrap",
                          lineHeight: "1.7",
                        }}
                      >
                        {viewModal.optitem_desc ||
                          "No details available."}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="um-modal-foot">
                <button
                  type="button"
                  className="um-btn-secondary"
                  onClick={() => setViewModal(null)}
                >
                  Close
                </button>

                <button
                  type="button"
                  className="um-btn-primary"
                  onClick={() => {
                    const selected = viewModal;

                    setViewModal(null);

                    setTimeout(() => {
                      startEdit(selected);
                    }, 100);
                  }}
                >
                  <FiEdit />
                  Edit Optional Extra
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {delModal && (
          <div
            className="um-overlay"
            onClick={() => setDelModal(null)}
          >
            <div
              className="um-modal um-delete-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="delete-content">
                {/* Icon */}
                <div className="delete-icon">
                  <FiTrash2 />
                </div>

                {/* Title */}
                <h2 className="delete-title">
                  Delete Optional Extra?
                </h2>

                {/* Name */}
                <div className="delete-badges">
                  <span className="badge badge-success">
                    {delModal.optitem_name}
                  </span>
                </div>

                {/* Description */}
                <p className="delete-description">
                  This optional extra will be permanently
                  deleted.
                  <br />
                  <strong>
                    This action cannot be undone.
                  </strong>
                </p>

                {/* Actions */}
                <div className="delete-actions">
                  <button
                    type="button"
                    className="um-btn-secondary"
                    onClick={() => setDelModal(null)}
                    disabled={delSaving}
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    disabled={delSaving}
                    className="delete-confirm-btn"
                    onClick={handleDelete}
                  >
                    {delSaving ? (
                      <span className="spinner" />
                    ) : (
                      <>
                        <FiTrash2 size={16} />
                        Delete Permanently
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

    
      </main>
    </Layout>
  );
}
